import { MapPin, UtensilsCrossed, ShoppingBag, Bike } from "lucide-react";
import { Section } from "@/components/Section";
import { useHomepageContent } from "@/hooks/useHomepageContent";
import { HOMEPAGE_FALLBACKS } from "@/lib/homepage-defaults";
import { useImageFallback } from "@/hooks/useImageFallback";

const ICONS = [MapPin, UtensilsCrossed, ShoppingBag, Bike];

export function HowItWorks() {
  const content = useHomepageContent();
  const image = useImageFallback(HOMEPAGE_FALLBACKS.howItWorksImageUrl);

  const steps = [
    { title: content.howItWorksStep1Title, body: content.howItWorksStep1Description },
    { title: content.howItWorksStep2Title, body: content.howItWorksStep2Description },
    { title: content.howItWorksStep3Title, body: content.howItWorksStep3Description },
    { title: content.howItWorksStep4Title, body: content.howItWorksStep4Description },
  ];

  return (
    <Section>
      <div className="max-w-2xl">
        <div className="mb-3 h-px w-10 bg-primary/40" aria-hidden />
        <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
          {content.howItWorksEyebrow}
        </span>
        <h2 className="mt-3 font-display text-4xl font-medium md:text-5xl">
          {content.howItWorksHeading}
        </h2>
        {content.howItWorksDescription ? (
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            {content.howItWorksDescription}
          </p>
        ) : null}
      </div>

      <div className="mt-10 grid items-start gap-10 lg:grid-cols-2 lg:gap-14">
        <div className="overflow-hidden rounded-3xl ring-1 ring-border">
          <img
            src={content.howItWorksImageUrl}
            alt={content.howItWorksImageAlt}
            width={1200}
            height={900}
            loading="lazy"
            decoding="async"
            onError={image.onError}
            className="aspect-[4/3] h-full w-full object-cover"
          />
        </div>

        <div className="grid gap-6 sm:grid-cols-2">
          {steps.map(({ title, body }, i) => {
            const Icon = ICONS[i];
            return (
              <div
                key={i}
                className="rounded-2xl border border-border bg-card p-6 transition-all duration-300 ease-out hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md motion-reduce:transform-none motion-reduce:transition-none"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-secondary to-background text-primary ring-1 ring-border">
                  <Icon size={20} strokeWidth={1.75} aria-hidden />
                </div>
                <div className="mt-4 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  Step {i + 1}
                </div>
                <h3 className="mt-2 font-display text-xl font-medium">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
}
